/*!
 * EGT* Film Studio — shared builders for the homepage hero and the /studio page
 * (founder notes 2026-10-24). Pure UA/JSON, no fs/path (client-registry safe).
 *
 * - hero(): a full-viewport loop of real scenes from the EGT* intro (40 scenes,
 *   4K finishes cut to web loops: a wide cut for landscape, a vertical cut for
 *   portrait phones). The client (/src/egt.filmstudio.js) picks the cut; reduced
 *   motion / save-data keep the still. Frosted card, never a scrim.
 * - requestAccess(): the "Request Access Today" email box. It hands the address
 *   to the existing platform reserve wizard on /reserve (EGT* is part of the
 *   platform): email → Next → the typed code → Next → reserve. The address rides
 *   sessionStorage, never the URL.
 */
var S = require("./egt.sections.js");

/* EGT with the gold asterisk, as the wordmark reads everywhere */
function mark() { return S.egtMark(); }
exports.mark = mark;

function requestAccess(opts) {
  opts = opts || {};
  var id = opts.id || "egt-fs-req";
  return {
    n: "form", c: "egt-fs-req" + (opts.c ? " " + opts.c : ""), id: id, action: "/reserve#reserve-wizard", method: "get",
    "data-egt-request-access": "true", novalidate: "novalidate",
    i: [
      { n: "p", c: "egt-fs-req__k", i: opts.label || "Request Access Today" },
      { n: "div", c: "egt-fs-req__row",
        i: [
          { n: "label", c: "visually-hidden", "for": id + "-email", i: "Email address" },
          /* no name attribute: without JS the form opens the wizard, and the address
           * never lands in a URL */
          { n: "input", c: "egt-fs-req__input", id: id + "-email", type: "email",
            autocomplete: "email", inputmode: "email", required: "required", placeholder: "Email address" },
          { n: "button", c: "egt-fs-req__btn", type: "submit", i: "Submit" }
        ] },
      { n: "p", c: "egt-fs-req__msg", role: "status", "aria-live": "polite", "data-egt-request-msg": "true", i: "" }
    ]
  };
}
exports.requestAccess = requestAccess;

/* the quiet second door under the ask: a frosted chip with a film still, a title
 * and a gold arrow. No fill, so the gold Submit stays the one call to action.
 * opts: { href, t, sub, thumb } */
function tour(opts) {
  return { n: "a", c: "egt-fs-tour", href: opts.href, "aria-label": opts.t + " — " + opts.sub,
    i: [
      { n: "span", c: "egt-fs-tour__thumb", "aria-hidden": "true",
        i: { n: "img", src: opts.thumb, alt: "", loading: "lazy", decoding: "async" } },
      { n: "span", c: "egt-fs-tour__txt",
        i: [ { n: "span", c: "egt-fs-tour__t", i: opts.t }, { n: "span", c: "egt-fs-tour__sub", i: opts.sub } ] },
      { n: "span", c: "egt-fs-tour__go", "aria-hidden": "true", i: "→" }
    ] };
}
exports.tour = tour;

/* opts: { id, loop ("film-home"|"film-studio"), eyebrow, title, lead, reqLabel, next, tour,
 *         side (the /studio layout: the video fills the stage and the words sit in a
 *         frosted panel down the right edge, behind a colour guardrail), caption } */
function hero(opts) {
  var base = "/vis/film-studio/" + opts.loop;
  var side = opts.side === true;
  return {
    n: "section", c: "egt-fs-hero" + (side ? " egt-fs-hero--side" : ""), id: opts.id,
    i: [
      { n: "div", c: "egt-fs-hero__media", "aria-hidden": "true",
        i: [
          { n: "picture",
            i: [
              { n: "source", media: "(orientation: portrait)", srcset: base + "-9x16.jpg" },
              { n: "img", c: "egt-fs-hero__poster", src: base + "-16x9.jpg", alt: "",
                fetchpriority: "high", decoding: "async" }
            ] },
          { n: "video", c: "egt-fs-hero__video", muted: true, loop: true, playsinline: true,
            preload: "none", "data-egt-fs-loop": "true",
            "data-wide": base + "-16x9.mp4", "data-tall": base + "-9x16.mp4" }
        ] },
      opts.caption ? { n: "p", c: "egt-fs-hero__caption", i: opts.caption } : null,
      { n: "div", c: side ? "egt-fs-hero__inner egt-fs-hero__inner--side" : "egt__shell egt-fs-hero__inner",
        i: { n: "div", c: side ? "egt-fs-hero__panel" : "egt-glass egt-fs-hero__card egt-reveal",
          i: [
            /* the side panel's colour guardrail, with a small arrow down to the next section */
            side ? { n: "span", c: "egt-fs-hero__rail", "aria-hidden": "true" } : null,
            side && opts.next ? { n: "a", href: opts.next, c: "egt-fs-hero__knob", "aria-label": "Scroll to the next section",
              i: { n: "span", "aria-hidden": "true", i: "⌄" } } : null,
            { n: "div", c: side ? "egt-fs-hero__panel-body egt-reveal" : "egt-fs-hero__body",
              i: [
                { n: "p", c: "egt__eyebrow egt-fs-hero__eyebrow", i: opts.eyebrow || [ mark(), " Film Studio" ] },
                { n: "h1", c: "egt-fs-hero__title", i: opts.title },
                opts.lead ? { n: "p", c: "egt-fs-hero__lead", i: opts.lead } : null,
                requestAccess({ id: opts.id + "-req", label: opts.reqLabel }),
                opts.tour ? tour(opts.tour) : null
              ].filter(Boolean) }
          ].filter(Boolean) } },
      !side && opts.next ? { n: "a", href: opts.next, c: "egt-hero__cue", "aria-label": "Scroll", i: "⌄" } : null
    ].filter(Boolean)
  };
}
exports.hero = hero;

function script() {
  return { n: "script", src: "/src/egt.filmstudio.js", type: "text/javascript", defer: "defer" };
}
exports.script = script;
